import { jsx, jsxs } from 'react/jsx-runtime';
import React__default, { useState, useRef, useEffect } from 'react';
import { styled } from 'styled-components';
import { pageResourceEmbeds } from '../expandedResource.config.js';
import { PageResourceMetadata } from './PageResourceMetadata.js';
import { useResourceBookmarkDataSafe } from '../../resource-detail/components/context/resourceDataContext.js';
import { useQueryPageResourceMarkdown } from '../../resource-detail/queries/useQueryPageResourceMarkdown.js';
import { Modal } from '@fabric-app/ui/components/Modal/Modal';
import { mediaMobile } from '@fabric-app/ui/constants/styled-utils';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import { useExpandedFdocContext } from '../../resource-detail/components/ExpandedFdoc/ExpandedFdocProvider.js';
import { ScrollArea } from '@fabric-app/ui/components/ScrollArea';
import { Flex } from '@fabric-app/ui/components/Flex';
import { useDomContentRect } from '@fabric-app/hooks/useDomContentRect';
import { useAsyncValue } from '@fabric-app/hooks/useAsyncValue';
import { validateImageUrl } from '@fabric-app/utils/files';
import { Tabs } from '@fabric-app/ui/components/Tabs';
import { Image } from '@fabric-app/ui/components/Image';
import { MarkdownRender } from '@fabric-app/ui/markdown/components/MarkdownRender';

const PaddedModalBody = styled(Modal.Body).withConfig({
  displayName: "PageResource__PaddedModalBody",
  componentId: "sc-9651f19b-0"
})([
  `padding-bottom:100px;min-height:100%;flex-grow:1;`
]);
const AbsoluteTabsList = styled(Tabs.List).withConfig({
  displayName: "PageResource__AbsoluteTabsList",
  componentId: "sc-9651f19b-1"
})([
  `position:absolute;bottom:20px;left:50%;transform:translateX(-50%);width:max-content;`,
  `{width:calc(100% - 2rem);}`
], mediaMobile);
const MarkdownContent = styled(MarkdownRender).withConfig({
  displayName: "PageResource__MarkdownContent",
  componentId: "sc-9651f19b-2"
})([
  `font-size:14px;max-width:490px;padding:16px;background:`,
  `;border-radius:6px;color:`,
  `;overflow-wrap:break-word;word-break:break-word;display:grid;> *{--margin-block-start:0.5em;--margin-block-end:0.5em;}`
], cssVar["color-bg-primary"], cssVar["color-text-tertiary"]);
const PageResource = () => {
  const { isMobileView } = useResponsive();
  const { setToolbarHeight } = useExpandedFdocContext();
  const { resource } = useResourceBookmarkDataSafe();
  const screenshotUrl = useAsyncValue(() => validateImageUrl(resource.data.webpage?.screenshot?.url));
  const [tab, setTab] = useState("metadata");
  const isEmbedded = !!resource.originUrl && !!pageResourceEmbeds.find((embed) => embed.matchFn(resource.originUrl));
  const { data: markdown } = useQueryPageResourceMarkdown(resource, {
    enabled: !isEmbedded
  });
  const containerRef = useRef(null);
  const [containerRect] = useDomContentRect(containerRef.current);
  const tabsListRef = useRef(null);
  const [tabsListRect] = useDomContentRect(tabsListRef.current);
  const preventArrowKeyNavigation = (e) => {
    if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
      e.preventDefault();
    }
  };
  const [imageFailed, setImageFailed] = useState(false);
  const showReaderView = !!markdown;
  const showScreenshotView = !imageFailed && screenshotUrl && !isEmbedded;
  const shouldSetToolbarHeight = Boolean(isMobileView || tabsListRect?.width && containerRect?.width && tabsListRect.width > containerRect.width * 0.75);
  React__default.useEffect(() => {
    if (shouldSetToolbarHeight) {
      setToolbarHeight(Math.abs((tabsListRect?.height || 0) + 20));
    }
  }, [
    shouldSetToolbarHeight,
    setToolbarHeight,
    tabsListRect?.height
  ]);
  useEffect(() => {
    if (tab === "reader" && !showReaderView) {
      setTab("metadata");
      return;
    } else if (tab === "screenshot" && !showScreenshotView) {
      setTab("metadata");
    }
  }, [
    showReaderView,
    showScreenshotView,
    tab
  ]);
  return /* @__PURE__ */ jsx(Tabs.Root, {
    value: tab,
    onValueChange: setTab,
    asChild: true,
    children: /* @__PURE__ */ jsx(ScrollArea.Default, {
      grow: true,
      ref: containerRef,
      innerDisplayFlex: true,
      "data-pan-id-ignore-resource-drag-resize": "xy",
      children: /* @__PURE__ */ jsxs(PaddedModalBody, {
        background: "tertiary",
        "data-pan-id-ignore-resource-drag-resize": "xy",
        children: [
          showReaderView && /* @__PURE__ */ jsx(Tabs.Content, {
            value: "readerView",
            asChild: true,
            children: /* @__PURE__ */ jsx(Flex, {
              alignItems: "center",
              direction: "column",
              flexGrow: true,
              justifyContent: "center",
              children: /* @__PURE__ */ jsx(MarkdownContent, {
                "data-id": "page-resource-markdown",
                markdown,
                parserOptions: {
                  fixMalformedLists: true,
                  fixSpacedStrong: true,
                  includeEmptyLines: false
                }
              })
            })
          }),
          showScreenshotView && /* @__PURE__ */ jsx(Tabs.Content, {
            value: "screenshot",
            asChild: true,
            children: /* @__PURE__ */ jsx(Flex, {
              alignItems: "center",
              direction: "column",
              flexGrow: true,
              justifyContent: "center",
              children: /* @__PURE__ */ jsx(Image, {
                src: screenshotUrl,
                alt: "Webpage screenshot",
                errorText: "No screenshot available.",
                onError: () => setImageFailed(true)
              })
            })
          }),
          /* @__PURE__ */ jsx(Tabs.Content, {
            value: "metadata",
            asChild: true,
            forceMount: true,
            children: /* @__PURE__ */ jsx(Flex, {
              alignItems: "center",
              direction: "column",
              flexGrow: true,
              children: /* @__PURE__ */ jsx(PageResourceMetadata, {})
            })
          }),
          (showReaderView || showScreenshotView) && /* @__PURE__ */ jsxs(AbsoluteTabsList, {
            ref: tabsListRef,
            children: [
              showReaderView && /* @__PURE__ */ jsx(Tabs.Trigger, {
                value: "readerView",
                onKeyDown: preventArrowKeyNavigation,
                tabIndex: 0,
                children: "Reader"
              }),
              showScreenshotView && /* @__PURE__ */ jsx(Tabs.Trigger, {
                value: "screenshot",
                onKeyDown: preventArrowKeyNavigation,
                tabIndex: 0,
                children: "Screenshot"
              }),
              /* @__PURE__ */ jsx(Tabs.Trigger, {
                value: "metadata",
                onKeyDown: preventArrowKeyNavigation,
                tabIndex: 0,
                children: "Metadata"
              })
            ]
          })
        ]
      })
    })
  });
};

export { PageResource };
//# sourceMappingURL=PageResource.js.map
